/**
 * Scenario Detail Page
 * 
 * Displays full details for a single roleplay scenario including:
 * - Name, category, intensity badges
 * - Setup and dialogue guides
 * - Description and page sections
 * - Related kinks, archetypes
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    ArrowLeft, Sparkles, Heart, Shield, Theater, Zap,
    ChevronRight, ChevronDown, Users, BookOpen,
    Film, Clock, Layers
} from 'lucide-react';
import api from '../services/api';
import Spinner from '../components/Spinner';

// Category colors
const getCategoryColor = (category) => {
    const colors = {
        'roleplay': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
        'fantasy': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
        'power': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        'taboo': 'text-rose-400 bg-rose-500/10 border-rose-500/20',
        'sensory': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        'medical': 'text-teal-400 bg-teal-500/10 border-teal-500/20'
    };
    return colors[category?.toLowerCase()] || 'text-gray-400 bg-gray-500/10 border-gray-500/20';
};

// Section configuration
const SECTION_CONFIG = {
    overview: { icon: BookOpen, title: 'Overview', color: 'text-blue-400' },
    setup_guide: { icon: Theater, title: 'Scene Setup', color: 'text-purple-400' },
    dialogue_examples: { icon: Users, title: 'Sample Dialogue', color: 'text-amber-400' },
    costume_suggestions: { icon: Sparkles, title: 'Costumes & Props', color: 'text-pink-400' },
    where_to_find: { icon: Film, title: 'Where to Find Content', color: 'text-wine-400' },
    media: { icon: Film, title: 'In Pop Culture', color: 'text-rose-400' },
    combinations: { icon: Layers, title: 'Pairs Well With', color: 'text-purple-400' },
    progression: { icon: ChevronRight, title: "What's Next", color: 'text-indigo-400' }
};

// Page section component with accordion
const PageSection = ({ sectionKey, content, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const config = SECTION_CONFIG[sectionKey] || { icon: BookOpen, title: sectionKey, color: 'text-gray-400' };
    const Icon = config.icon;

    if (!content) return null;

    return (
        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300 mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors focus:outline-none"
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <h3 className="font-fantasy font-semibold text-lg text-cream/90 tracking-wide">{config.title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-3 border-t border-wine-700/30">
                    <div className="prose prose-invert max-w-none text-cream/70 leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Related entity link component
const EntityLink = ({ to, name, category }) => (
    <Link
        to={to}
        className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
    >
        <div>
            <span className="text-white group-hover:text-wine-300 transition-colors">{name}</span>
            {category && <span className="ml-2 text-sm text-dark-500">{category}</span>}
        </div>
        <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
    </Link>
);

const ScenarioDetailPage = () => {
    const { id } = useParams();
    const [scenario, setScenario] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchScenario();
    }, [id]);

    const fetchScenario = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getScenario(id);
            setScenario(data);
        } catch (err) {
            console.error('Error fetching scenario:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading scenario...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !scenario) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">🎭</div>
                    <h2 className="text-xl font-bold text-white mb-2">Scenario Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This scenario does not exist.'}</p>
                    <Link to="/scenarios" className="btn-primary">
                        Back to Scenarios
                    </Link>
                </div>
            </div>
        );
    }

    // Get page sections by key
    const getSectionContent = (key) => {
        const section = scenario.pageSections?.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    const categoryClass = getCategoryColor(scenario.category);

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/scenarios"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Scenarios</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {scenario.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/scenario/${scenario.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {scenario.parentVariant.name}
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <header className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-700/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-purple-700/20 flex items-center justify-center">
                                <Theater size={28} className="text-purple-400" />
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-3 tracking-wide">
                                    {scenario.name || scenario.scenarioDescription}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    {scenario.category && (
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryClass}`}>
                                            {scenario.category}
                                        </span>
                                    )}
                                    {scenario.intensity && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-rose-500/10 text-rose-400">
                                            <Zap size={12} className="inline mr-1" />
                                            Intensity {scenario.intensity}/10
                                        </span>
                                    )}
                                    {scenario.preparation_time && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-amber-500/10 text-amber-400">
                                            <Clock size={12} className="inline mr-1" />
                                            {scenario.preparation_time} prep
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        {scenario.description && (
                            <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed mt-4">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{scenario.description}</ReactMarkdown>
                            </div>
                        )}

                        {/* Ornate Divider */}
                        <div className="ornate-divider w-32 my-6" />

                        {/* Key Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Zap size={18} className="mx-auto mb-1 text-rose-400" />
                                <div className="text-lg font-bold text-cream">{scenario.intensity || 5}</div>
                                <div className="text-xs text-dark-400">Intensity</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Theater size={18} className="mx-auto mb-1 text-purple-400" />
                                <div className="text-lg font-bold text-cream">{scenario.complexity || 5}</div>
                                <div className="text-xs text-dark-400">Complexity</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Users size={18} className="mx-auto mb-1 text-blue-400" />
                                <div className="text-lg font-bold text-cream">{scenario.participants || 2}</div>
                                <div className="text-xs text-dark-400">Participants</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Heart size={18} className="mx-auto mb-1 text-pink-400" />
                                <div className="text-lg font-bold text-cream">{scenario.intimacy || 5}</div>
                                <div className="text-xs text-dark-400">Intimacy</div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Sections */}
                <div className="space-y-4">
                    {Object.keys(SECTION_CONFIG).map(key => (
                        <PageSection
                            key={key}
                            sectionKey={key}
                            content={getSectionContent(key)}
                            defaultOpen={key === 'overview' || key === 'setup_guide'}
                        />
                    ))}
                </div>

                {/* Related Entities */}
                {scenario.relatedKinks?.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-wine-400" />
                            Related Kinks
                        </h3>
                        <div className="grid gap-2">
                            {scenario.relatedKinks.map(kink => (
                                <EntityLink
                                    key={kink.id}
                                    to={`/kink/${kink.id}`}
                                    name={kink.name}
                                    category={kink.category}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {scenario.relatedArchetypes?.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-400" />
                            Compatible Archetypes
                        </h3>
                        <div className="grid gap-2">
                            {scenario.relatedArchetypes.map(arch => (
                                <EntityLink
                                    key={arch.id}
                                    to={`/archetype/${arch.id}`}
                                    name={arch.name}
                                    category={arch.category}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Sub-Variants */}
                {scenario.childVariants && scenario.childVariants.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Layers className="w-5 h-5 text-purple-400" />
                            Variants of {scenario.name || scenario.scenarioDescription}
                        </h3>
                        <div className="grid gap-2">
                            {scenario.childVariants.map(variant => (
                                <EntityLink
                                    key={variant.id}
                                    to={`/scenario/${variant.id}`}
                                    name={variant.name}
                                    category={variant.category}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ScenarioDetailPage;
